import { toPng } from 'html-to-image';

/**
 * Obtiene las dimensiones reales de un elemento SVG incluyendo todo su contenido
 * @param {Element} svgElement - El elemento SVG
 * @returns {Object} - Objeto con width y height
 */
function getSvgRealDimensions(svgElement) {
  try {
    // Obtener el bounding box del contenido del SVG
    const bbox = svgElement.getBBox();

    // Obtener dimensiones del elemento renderizado
    const rect = svgElement.getBoundingClientRect();

    // Usar la mayor de las dimensiones para asegurar que todo se capture
    const width = Math.max(bbox.width, rect.width, 400); // Mínimo 400px
    const height = Math.max(bbox.height, rect.height, 300); // Mínimo 300px

    return { width, height };
  } catch {
    // Fallback si getBBox() falla
    const rect = svgElement.getBoundingClientRect();
    return {
      width: rect.width || 800,
      height: rect.height || 600,
    };
  }
}

/**
 * Descarga un elemento SVG como imagen PNG usando html-to-image
 * @param {Element} svgElement - El elemento SVG a descargar
 * @param {Object} options - Opciones de configuración
 * @param {string} options.filename - Nombre del archivo (sin extensión)
 * @param {number} options.scale - Factor de escala para mejor calidad (default: 2)
 * @param {string} options.backgroundColor - Color de fondo (default: 'white')
 * @param {number} options.padding - Padding extra para evitar cortes (default: 20)
 * @param {number} options.width - Ancho personalizado (opcional)
 * @param {number} options.height - Alto personalizado (opcional)
 * @returns {Promise<void>}
 */
export async function downloadSvgAsPng(svgElement, options = {}) {
  const { filename = `diagram-${Date.now()}`, scale = 2, backgroundColor = 'white', padding = 20, width: customWidth, height: customHeight } = options;

  try {
    if (!svgElement) {
      throw new Error('No se encontró el elemento SVG');
    }

    // Obtener dimensiones reales del SVG
    const { width: svgWidth, height: svgHeight } = getSvgRealDimensions(svgElement);

    // Usar dimensiones personalizadas o las calculadas, agregando padding
    const finalWidth = customWidth || svgWidth + padding * 2;
    const finalHeight = customHeight || svgHeight + padding * 2;

    // Configuración para html-to-image
    const config = {
      backgroundColor,
      pixelRatio: scale,
      width: finalWidth,
      height: finalHeight,
      style: {
        padding: `${padding}px`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    };

    // Convertir SVG a PNG usando html-to-image
    const dataUrl = await toPng(svgElement, config);

    // Crear enlace de descarga
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}.png`;

    // Agregar temporalmente al DOM y hacer click
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(`Error al descargar la imagen: ${error.message}`);
  }
}

/**
 * Descarga un SVG desde un contenedor que lo contiene
 * Mejor opción para evitar problemas de dimensiones
 * @param {Element} container - El contenedor que contiene el SVG
 * @param {Object} options - Opciones de configuración (ver downloadSvgAsPng)
 * @returns {Promise<void>}
 */
export async function downloadSvgFromContainer(container, options = {}) {
  if (!container) {
    throw new Error('No se proporcionó un contenedor válido');
  }

  // En lugar de buscar solo el SVG, capturar todo el contenedor
  // Esto evita problemas de dimensiones y cortes
  const { filename = `diagram-${Date.now()}`, scale = 2, backgroundColor = 'white', padding = 20 } = options;

  try {
    // Obtener dimensiones del contenedor
    const rect = container.getBoundingClientRect();
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    // Configuración para capturar el contenedor completo
    const config = {
      backgroundColor,
      pixelRatio: scale,
      width,
      height,
      style: {
        padding: `${padding}px`,
        boxSizing: 'border-box',
      },
    };

    // Convertir contenedor completo a PNG
    const dataUrl = await toPng(container, config);

    // Crear enlace de descarga
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}.png`;

    // Agregar temporalmente al DOM y hacer click
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(`Error al descargar la imagen: ${error.message}`);
  }
}

/**
 * Descarga un elemento completo del DOM como imagen PNG
 * Útil para contenedores que incluyen más que solo el SVG
 * @param {Element} element - El elemento a descargar
 * @param {Object} options - Opciones de configuración
 * @returns {Promise<void>}
 */
export async function downloadElementAsPng(element, options = {}) {
  const { filename = `element-${Date.now()}`, scale = 2, backgroundColor = 'white', padding = 20 } = options;

  try {
    if (!element) {
      throw new Error('No se encontró el elemento');
    }

    // Obtener dimensiones del elemento
    const rect = element.getBoundingClientRect();
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    // Configuración para html-to-image
    const config = {
      backgroundColor,
      pixelRatio: scale,
      width,
      height,
      style: {
        padding: `${padding}px`,
        boxSizing: 'border-box',
      },
    };

    // Convertir elemento a PNG
    const dataUrl = await toPng(element, config);

    // Crear enlace de descarga
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}.png`;

    // Agregar temporalmente al DOM y hacer click
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(`Error al descargar la imagen: ${error.message}`);
  }
}
